import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { analyzePageWithGemini, submitTask, createTaskIssue, fetchTaskFromStitch, pushTaskToFigma, createDesignerTicket, selectTaskVariant, unlinkTaskIssue } from '@/services/redesigns';
import { openExternal } from '@/lib/embedBridge';
import { FlowStep } from './FlowStep';
import { DesignCompare } from './DesignCompare';
import { NotesInput } from './NotesInput';
import type { Redesign, RedesignPage, RedesignTask } from '@/types';

function humanizeRoute(route: string): string {
  return route.replace(/^\//, '').split('/').filter(Boolean)
    .map(seg => seg.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    .join(' / ') || 'Home';
}

interface StitchFlowProps {
  redesign: Redesign;
  page: RedesignPage;
  pageIndex: number;
  task: RedesignTask;
  onBack: () => void;
}

type StitchStep = 'analyze' | 'submit' | 'redesigning' | 'review' | 'done';

function deriveStep(page: RedesignPage, task: RedesignTask): StitchStep {
  if (task.implementationIssue) return 'done';
  if (task.status === 'complete') return 'review';
  if (task.status === 'redesigning') return 'redesigning';
  if (page.geminiStatus !== 'success') return 'analyze';
  return 'submit';
}

export function StitchFlow({ redesign, page, pageIndex, task, onBack }: StitchFlowProps) {
  const queryClient = useQueryClient();
  const redesignId = redesign._id;
  const step = deriveStep(page, task);
  const [notes, setNotes] = useState(task.designerNotes || '');
  const [engineerNotes, setEngineerNotes] = useState('');
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(() => {
    const idx = task.page?.variants?.findIndex(v => v.selected) ?? -1;
    return idx >= 0 ? idx : 0;
  });
  const pageLabel = page.title || humanizeRoute(task.pageRef.route);
  const [implTicketTitle, setImplTicketTitle] = useState(`[Implement] ${redesign.repo} — ${pageLabel}`);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['redesign-tasks', redesignId] });
    queryClient.invalidateQueries({ queryKey: ['redesign', redesignId] });
  };

  const analyzeMutation = useMutation({
    mutationFn: () => analyzePageWithGemini(redesignId, pageIndex),
    onSuccess: invalidate,
    onError: invalidate,
  });

  const submitMutation = useMutation({
    mutationFn: () => submitTask(redesignId, task._id),
    onSuccess: invalidate,
  });

  const selectVariantMutation = useMutation({
    mutationFn: (variantIndex: number) => selectTaskVariant(redesignId, task._id, variantIndex),
    onSuccess: invalidate,
  });

  const refetchStitchMutation = useMutation({
    mutationFn: () => fetchTaskFromStitch(redesignId, task._id),
    onSuccess: invalidate,
  });

  const [figmaFileKey, setFigmaFileKey] = useState('');
  const [designerGuidance, setDesignerGuidance] = useState('');
  const [designerTicketTitle, setDesignerTicketTitle] = useState(`[Design Review] ${redesign.repo} — ${pageLabel}`);

  const pushToFigmaMutation = useMutation({
    mutationFn: () => pushTaskToFigma(redesignId, task._id, figmaFileKey, `${task.pageRef.route} (${task.pageRef.viewport})`),
    onSuccess: invalidate,
  });

  const createDesignerTicketMutation = useMutation({
    mutationFn: () => createDesignerTicket(redesignId, task._id, designerGuidance.trim() || undefined, designerTicketTitle.trim() || undefined),
    onSuccess: invalidate,
  });

  const unlinkIssueMutation = useMutation({
    mutationFn: () => unlinkTaskIssue(redesignId, task._id),
    onSuccess: invalidate,
  });

  const createIssueMutation = useMutation({
    mutationFn: (params: { notes?: string; title?: string }) => createTaskIssue(redesignId, task._id, params.notes, params.title),
    onSuccess: invalidate,
  });

  const handleOpenHtml = useCallback(() => {
    const variants = task.page?.variants ?? [];
    const activeVariant = variants[selectedVariantIdx] ?? variants[0];
    // Prefer the selected variant's HTML URL; fall back to the stored code export
    const codeUrl = activeVariant?.codeUrl;
    if (codeUrl) {
      window.open(codeUrl, '_blank');
      return;
    }
    const html = task.page?.codeExport?.html;
    if (html) {
      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); }
    }
  }, [task.page?.variants, task.page?.codeExport?.html, selectedVariantIdx]);

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 18 }}>&larr;</span> Back to pages
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
          {page.route} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({page.viewport})</span>
        </h2>
        <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 10px', borderRadius: 10, color: '#1e40af', background: '#dbeafe' }}>
          AI Explore
        </span>
      </div>

      {/* Step: Analyze */}
      {step === 'analyze' && (
        <FlowStep stepLabel="Step 1 of 3 - Analyze Page">
          <div style={{ display: 'grid', gridTemplateColumns: page.originalScreenshotUrl ? '200px 1fr' : '1fr', gap: 20, alignItems: 'start' }}>
            {page.originalScreenshotUrl && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Current Page</div>
                <img src={page.originalScreenshotUrl} alt="Current page" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--bg-secondary)' }} />
              </div>
            )}
            <div>
              <p style={{ fontSize: 14, color: '#4b5563', margin: '0 0 10px', lineHeight: 1.6 }}>
                Gemini will analyze the page to understand its layout and components. This guides Stitch in generating a redesigned version.
              </p>

              <NotesInput
                label="Redesign Notes"
                placeholder="e.g. Make it more modern, brighter colors, simplify the navigation..."
                value={notes}
                onChange={setNotes}
              />

              <div style={{ marginTop: 16 }}>
                <button
                  onClick={() => analyzeMutation.mutate()}
                  disabled={analyzeMutation.isPending}
                  style={{
                    padding: '10px 24px', background: analyzeMutation.isPending ? 'var(--bg-disabled)' : 'var(--color-primary)',
                    color: 'white', border: 'none', borderRadius: 8, cursor: analyzeMutation.isPending ? 'not-allowed' : 'pointer',
                    fontWeight: 600, fontSize: 14,
                  }}
                >
                  {analyzeMutation.isPending ? 'Analyzing...' : page.geminiStatus === 'failed' ? 'Retry Analysis' : 'Analyze with Gemini'}
                </button>
              </div>

              {page.geminiStatus === 'failed' && page.geminiError && (
                <div style={{ marginTop: 10, padding: 10, background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
                  {page.geminiError}
                </div>
              )}
            </div>
          </div>
        </FlowStep>
      )}

      {/* Step: Submit */}
      {step === 'submit' && (
        <FlowStep stepLabel="Step 2 of 3 - Submit to Stitch">
          <DesignCompare beforeUrl={page.originalScreenshotUrl} beforeLabel="Current Page" compact />

          {page.visionAnalysis && (
            <div style={{ padding: 14, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', marginBottom: 16, maxHeight: 150, overflow: 'auto' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 6 }}>Analysis Summary</div>
              <div style={{ fontSize: 12, color: '#14532d', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{page.visionAnalysis.slice(0, 500)}{page.visionAnalysis.length > 500 ? '...' : ''}</div>
            </div>
          )}

          {notes && (
            <div style={{ padding: 12, background: 'var(--color-primary-light)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#1e40af' }}>
              <strong>Your notes:</strong> {notes}
            </div>
          )}

          <button
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending}
            style={{
              padding: '12px 28px', background: submitMutation.isPending ? 'var(--bg-disabled)' : 'var(--color-primary)',
              color: 'white', border: 'none', borderRadius: 8, cursor: submitMutation.isPending ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 14,
            }}
          >
            {submitMutation.isPending ? 'Submitting...' : task.status === 'failed' ? 'Retry' : 'Submit to Stitch'}
          </button>

          {task.error && task.status === 'failed' && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>{task.error}</div>
          )}
        </FlowStep>
      )}

      {/* Step: Redesigning (in progress) */}
      {step === 'redesigning' && (
        <FlowStep stepLabel="Step 2 of 3 - Redesigning">
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid var(--color-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
            <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--color-primary)' }}>Stitch is generating your redesign...</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>This may take a few minutes.</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </FlowStep>
      )}

      {/* Step: Review */}
      {step === 'review' && (() => {
        const variants = task.page?.variants ?? [];
        const activeVariant = variants[selectedVariantIdx] ?? variants[0];
        return (
        <FlowStep stepLabel="Step 3 of 3 - Review Result">
          <DesignCompare
            beforeUrl={page.originalScreenshotUrl}
            beforeLabel="Before"
            afterUrl={activeVariant?.imageUrl}
            afterLabel={activeVariant?.label || 'After (Stitch)'}
          />

          {/* Variant selector — only shown when multiple variants exist */}
          {variants.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {variants.map((v, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setSelectedVariantIdx(i);
                    selectVariantMutation.mutate(i);
                  }}
                  style={{
                    padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                    border: i === selectedVariantIdx ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                    background: i === selectedVariantIdx ? 'var(--color-primary-light)' : 'var(--color-card-surface)',
                    color: i === selectedVariantIdx ? 'var(--color-primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  {v.label || `Design ${i + 1}`}
                </button>
              ))}
            </div>
          )}

          {task.page?.codeExport?.html && (
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <button onClick={handleOpenHtml} style={{ padding: '8px 16px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Open in new tab</button>
            </div>
          )}

          {task.stitchProjectId && (
            <div style={{ marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 16 }}>
              <a href={`https://stitch.withgoogle.com/projects/${task.stitchProjectId}`} onClick={e => { e.preventDefault(); openExternal(`https://stitch.withgoogle.com/projects/${task.stitchProjectId}`); }} style={{ color: 'var(--color-primary)', cursor: 'pointer' }}>View in Stitch</a>
              <button
                onClick={() => refetchStitchMutation.mutate()}
                disabled={refetchStitchMutation.isPending}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                  border: '1px solid var(--color-border)', background: 'var(--color-card-surface)',
                  color: 'var(--text-secondary)', cursor: refetchStitchMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {refetchStitchMutation.isPending ? 'Fetching...' : 'Re-fetch from Stitch'}
              </button>
            </div>
          )}

          {/* Figma → Designer path (optional refinement before engineer) */}
          {!task.designerIssue && (
            <div style={{ marginBottom: 20, padding: 16, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Want a designer to refine this first?
              </div>

              {!task.figmaDesign?.status || task.figmaDesign.status !== 'complete' ? (
                <>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
                    Push the selected design to Figma, then create a ticket for a designer to do a pixel-perfect refinement.
                  </p>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Figma File Key</label>
                      <input
                        type="text"
                        value={figmaFileKey}
                        onChange={e => setFigmaFileKey(e.target.value)}
                        placeholder="Paste Figma file key..."
                        style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <button
                      onClick={() => pushToFigmaMutation.mutate()}
                      disabled={pushToFigmaMutation.isPending || !figmaFileKey.trim()}
                      style={{
                        padding: '8px 16px', fontSize: 12, fontWeight: 500, borderRadius: 6, whiteSpace: 'nowrap',
                        border: 'none', background: pushToFigmaMutation.isPending || !figmaFileKey.trim() ? 'var(--bg-disabled)' : '#7c3aed',
                        color: 'white', cursor: pushToFigmaMutation.isPending || !figmaFileKey.trim() ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {pushToFigmaMutation.isPending ? 'Pushing...' : 'Push to Figma'}
                    </button>
                  </div>
                  {pushToFigmaMutation.isError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>{(pushToFigmaMutation.error as Error).message}</div>
                  )}
                  {pushToFigmaMutation.isSuccess && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#059669' }}>Pushed to Figma. You can now send to a designer.</div>
                  )}
                </>
              ) : (
                <>
                  <p style={{ fontSize: 12, color: '#059669', margin: '0 0 10px' }}>
                    Design pushed to Figma. Create a ticket for your designer to refine it.
                  </p>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Ticket Title</label>
                    <input
                      type="text"
                      value={designerTicketTitle}
                      onChange={e => setDesignerTicketTitle(e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--color-border)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                  <NotesInput
                    label="Guidance for Designer"
                    placeholder="e.g. Match the brand colors exactly, refine spacing on the card grid..."
                    value={designerGuidance}
                    onChange={setDesignerGuidance}
                  />
                  <button
                    onClick={() => createDesignerTicketMutation.mutate()}
                    disabled={createDesignerTicketMutation.isPending}
                    style={{
                      marginTop: 10, padding: '8px 16px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                      border: 'none', background: createDesignerTicketMutation.isPending ? 'var(--bg-disabled)' : '#7c3aed',
                      color: 'white', cursor: createDesignerTicketMutation.isPending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {createDesignerTicketMutation.isPending ? 'Creating...' : 'Send to Designer'}
                  </button>
                  {createDesignerTicketMutation.isError && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626' }}>{(createDesignerTicketMutation.error as Error).message}</div>
                  )}
                </>
              )}
            </div>
          )}

          {task.designerIssue && (
            <div style={{ marginBottom: 20, padding: 12, background: '#f5f3ff', borderRadius: 8, border: '1px solid #c4b5fd', fontSize: 13 }}>
              Designer ticket: <a href={task.designerIssue.linearIssueUrl} onClick={e => { e.preventDefault(); openExternal(task.designerIssue!.linearIssueUrl); }} style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer' }}>{task.designerIssue.linearIdentifier}</a>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>— Once the designer finishes, create the engineer issue below.</span>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Issue Title
            </label>
            <input
              type="text"
              value={implTicketTitle}
              onChange={e => setImplTicketTitle(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <NotesInput
            label="Notes for Engineer"
            placeholder="e.g. Use the existing CardGrid component, watch responsive breakpoints at tablet..."
            value={engineerNotes}
            onChange={setEngineerNotes}
          />

          <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
            <button
              onClick={() => createIssueMutation.mutate({ notes: engineerNotes.trim() || undefined, title: implTicketTitle.trim() || undefined })}
              disabled={createIssueMutation.isPending}
              style={{
                padding: '12px 28px', background: createIssueMutation.isPending ? 'var(--bg-disabled)' : 'var(--color-success)',
                color: 'white', border: 'none', borderRadius: 8, cursor: createIssueMutation.isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14,
              }}
            >
              {createIssueMutation.isPending ? 'Creating...' : 'Create Linear Issue'}
            </button>
          </div>

          {createIssueMutation.isError && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
              {(createIssueMutation.error as Error).message}
            </div>
          )}
        </FlowStep>
        );
      })()}

      {/* Step: Done */}
      {step === 'done' && (
        <FlowStep stepLabel="Complete">
          <DesignCompare
            beforeUrl={page.originalScreenshotUrl}
            beforeLabel="Before"
            afterUrl={task.page?.variants?.find(v => v.selected)?.imageUrl || task.page?.variants?.[0]?.imageUrl}
            afterLabel="After (Stitch)"
          />

          <div style={{ padding: 16, background: '#ecfdf5', borderRadius: 8, border: '1px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#065f46', marginBottom: 4 }}>Implementation Issue Created</div>
              <a
                href={task.implementationIssue!.linearIssueUrl}
                onClick={e => { e.preventDefault(); openExternal(task.implementationIssue!.linearIssueUrl); }}
                style={{ color: '#065f46', fontWeight: 500, cursor: 'pointer', fontSize: 14 }}
              >
                {task.implementationIssue!.linearIdentifier}
              </a>
            </div>
            <button
              onClick={() => unlinkIssueMutation.mutate()}
              disabled={unlinkIssueMutation.isPending}
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 6,
                border: '1px solid #fca5a5', background: 'white',
                color: '#dc2626', cursor: unlinkIssueMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {unlinkIssueMutation.isPending ? 'Removing...' : 'Remove Issue'}
            </button>
          </div>
        </FlowStep>
      )}
    </div>
  );
}
